import { Stack } from 'component-library';
import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { ChartLegendBar } from '@/components/Charts';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { ChartProps } from '@/components/Charts/types';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import {
  Bar,
  BarChart,
  Chart,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
  TooltipProps,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/Chart';
import { isNullish } from '@/utils/booleans';
import { printYear } from '@/utils/formatting/date';

export type SpeciesChangeChartData = {
  group: string;
} & {
  [year: number]: number;
};

type SpeciesChangeChart = HTMLAttributes<HTMLDivElement> & {
  data: SpeciesChangeChartData[];
} & Omit<ChartProps, 'data'>;

const YEARS_COLOR_MAP: Record<string, string> = {
  '2018': '#E31A1C',
  '2019': '#FF7F00',
  '2020': '#A6CEE3',
  '2021': '#1F78B4',
  '2022': '#B2DF8A',
  '2023': '#6ab4c2',
  '2024': '#2be2a2',
  '2025': '#6A3D9A',
};

export const SpeciesOccupancyHistoricalChart: FC<SpeciesChangeChart> = ({ data, ...delegated }) => {
  const { t } = useTranslation();

  const yearsInTimeSeries = Array.from(
    new Set<string>(data.flatMap((d) => Object.keys(d).filter((key) => key !== 'group'))),
  );

  return (
    <>
      <Chart.Container {...delegated} data={data} config={{}} data-testid='species-occupancy-chart'>
        <BarChart data={data} barGap={0}>
          {yearsInTimeSeries.map((year) => (
            <Bar key={year} dataKey={year} fill={YEARS_COLOR_MAP[`${year}`]} className='species-occupancy-bar' />
          ))}
          <Chart.XAxis
            type='category'
            padding={{ left: 10, right: 10 }}
            dataKey='group'
            className='species-occupancy-x-axis'
            interval={0}
            angle={-45}
            textAnchor='end'
            height={100}
            dy={-10}
          />
          <Chart.YAxis domain={[0, 1]} className='species-occupancy-y-axis'>
            <Chart.YAxisLabel>{t('shared.ncaDetail.indicatorSpecies.occupancy.value')}</Chart.YAxisLabel>
          </Chart.YAxis>
          <Chart.Tooltip
            cursor={{
              fill: 'transparent',
              stroke: 'rgba(0, 0, 0, 0.6)', // neutral-black-60
              strokeWidth: 1,
            }}
            content={(props) => <CustomTooltip {...props} />}
          />
        </BarChart>
      </Chart.Container>
      <CustomLegendBar years={yearsInTimeSeries} />
    </>
  );
};

const CustomLegendBar = ({ years }: { years: string[] }) => {
  const legendBarItems: ChartLegendBarItem[] = years.map((year) => ({
    label: year,
    color: YEARS_COLOR_MAP[`${year}`] ?? '#000000',
    shape: 'column',
  }));

  return (
    <ChartLegendBar items={legendBarItems} className='sm:ml-[28px]' data-testid='species-occupancy-chart-legend' />
  );
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) return null;

  const yearsData = payload.map((data) => ({
    year: data.dataKey,
    value: !isNullish(data.value)
      ? getDisplayNumber(data.value as string, window.navigator.language)
      : t('global.analysis.noData'),
  }));

  return (
    <Stack direction='col' spacing={1.5} className='rounded-md bg-white-100 p-2 elevation-1 min-w-[150px]'>
      <Stack spacing={2}>
        <ChartTooltipTextPrimary>{label}</ChartTooltipTextPrimary>
        {yearsData.map(({ year, value }) => (
          <Stack key={year} direction='row' className='my-0.5 justify-between'>
            <ChartTooltipTextSecondary>{printYear(year as string)}</ChartTooltipTextSecondary>
            <ChartTooltipTextSecondary>{value}</ChartTooltipTextSecondary>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

export { CustomTooltip as CustomTooltipSpeciesOccupancy };
